import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  styled
} from '@mui/material';
import DialogTitle from '../atoms/DialogTitle';

/**
 * Props for the ExportDialog component
 */
export interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  onExportWorkplace: () => void;
  onExportPDF: () => void;
}

/**
 * Styled components matching the application's dialog styling
 */
const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: '0 24px 16px 24px',
  '& .MuiTypography-root': {
    color: theme.palette.text.primary,
    fontSize: '14px',
    lineHeight: '1.4',
    fontFamily: 'Open Sans',
  },
}));

const ExportButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '12px',
  padding: '12px 16px',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  fontFamily: 'Open Sans',
  fontWeight: 600,
  fontSize: '14px',
  textTransform: 'none',
  width: '100%',
  height: '56px',
  marginBottom: '12px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child': {
    marginBottom: 0,
  },
}));

/**
 * ExportDialog component - A dialog for choosing export options
 * 
 * This component provides a dialog with two export options:
 * - Export as Workplace design
 * - Export as PDF
 * 
 * @param props - Component props
 * @returns ExportDialog component
 */
const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  onClose,
  onExportWorkplace,
  onExportPDF,
}) => {
  const handleExportWorkplace = () => {
    onExportWorkplace();
    onClose();
  };

  const handleExportPDF = () => {
    onExportPDF();
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Only close on escape key or close button click, not backdrop click
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      onKeyDown={handleKeyDown}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            border: theme => `0.6px solid ${theme.palette.divider}`,
            borderRadius: '8px',
            minWidth: '400px',
          },
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)', // For Safari support
        }
      }}
    >
      <DialogTitle onClose={onClose}>
        Export your designs
      </DialogTitle>
      
      <StyledDialogContent>
        <Typography variant="body1" sx={{ 
          marginBottom: '24px' }}>
          Export your designs to download and share with Workplace users outside your organization, or download a PDF version for viewing.
        </Typography>
        
        <Box>
          <ExportButton onClick={handleExportWorkplace}>
            <img
              src="/images/ExportWorkplace.svg"
              alt="Export as Workplace design"
              style={{ width: '24px', height: '24px' }}
            />
            Export as Workplace design
          </ExportButton>

          <ExportButton onClick={handleExportPDF}>
            <Box
              component="img"
              src="/images/ExportPDF.svg"
              alt="Export as PDF"
              sx={{
                width: '24px',
                height: '24px',
                filter: theme => theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
              }}
            />
            Export as PDF
          </ExportButton>
        </Box>
      </StyledDialogContent>
    </Dialog>
  );
};

export default ExportDialog;
